import { useState, useEffect, useCallback, useRef } from "react";
import { themeGet } from "@styled-system/theme-get";
import { useTheme } from "styled-components";
import { sum } from "lodash";

import { useWorkspaceFeatureFlags } from "src/contexts/global/WorkspaceContext";
import useWindowResizeListener from "src/hooks/useWindowResizeListener";

const APP_LAYOUT_HEADER_SELECTOR = "#app-layout-header";

/** Profile header roots (team / person / tag / goals) — at most one per route. */
export const PROFILE_HEADER_CONTAINER_IDS = [
  "#team-profile-header-container",
  "#person-profile-header-container",
  "#tag-profile-header-container",
  "#objective-profile-header-container",
];

const DEFAULT_SPACING_HEIGHT = 16;
const DEFAULT_FALLBACK_HEIGHT = 64;
const HEADER_POLL_INTERVAL_MS = 100;

const getProfileHeaderContainerElement = () => {
  for (const selector of PROFILE_HEADER_CONTAINER_IDS) {
    const el = document.querySelector(selector);
    if (el) {
      return el;
    }
  }
  return null;
};

const castToNumber = (value, defaultValue) =>
  parseFloat(value.replace(/[^0-9.]/g, "")) || defaultValue;

/**
 * Navbar + spacing + optional Nova profile container + app layout header height (px).
 *
 * @param {Object} [options]
 * @param {boolean} [options.includeProfileHeaders=true]
 * @param {boolean} [options.hideHeader=false]
 */
export const useAppHeadersHeight = (options = {}) => {
  const { includeProfileHeaders = true, hideHeader = false } = options;
  const [profileHeaderHeight, setProfileHeaderHeight] = useState(0);
  const [appLayoutHeaderHeight, setAppLayoutHeaderHeight] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const featureFlags = useWorkspaceFeatureFlags();
  const theme = useTheme();
  const observerRef = useRef(null);

  const updateProfileHeaderHeight = useCallback(() => {
    if (!includeProfileHeaders) {
      setProfileHeaderHeight(0);
      return;
    }

    const profileHeaderContainer = getProfileHeaderContainerElement();

    if (profileHeaderContainer) {
      const rect = profileHeaderContainer.getBoundingClientRect();
      setProfileHeaderHeight(rect.height);
    } else {
      setProfileHeaderHeight(0);
    }
  }, [includeProfileHeaders]);

  const updateAppLayoutHeaderHeight = useCallback(() => {
    if (hideHeader) {
      setAppLayoutHeaderHeight(0);
      setIsInitialized(true);
      return;
    }

    const appLayoutHeaderContainer = document.querySelector(
      APP_LAYOUT_HEADER_SELECTOR
    );

    if (appLayoutHeaderContainer) {
      const rect = appLayoutHeaderContainer.getBoundingClientRect();
      setAppLayoutHeaderHeight(rect.height);
      setIsInitialized(true);
    } else {
      setAppLayoutHeaderHeight(0);
    }
  }, [hideHeader]);

  const updateCalculatedHeights = useCallback(() => {
    updateProfileHeaderHeight();
    updateAppLayoutHeaderHeight();
  }, [updateProfileHeaderHeight, updateAppLayoutHeaderHeight]);

  useEffect(() => {
    let pollForHeadersId = null;

    function pollForHeaders() {
      const appLayoutHeaderContainer = hideHeader
        ? null
        : document.querySelector(APP_LAYOUT_HEADER_SELECTOR);
      const profileHeaderContainer = includeProfileHeaders
        ? getProfileHeaderContainerElement()
        : null;

      if (
        (hideHeader || appLayoutHeaderContainer) &&
        (!includeProfileHeaders || profileHeaderContainer)
      ) {
        updateCalculatedHeights();
      } else {
        pollForHeadersId = setTimeout(pollForHeaders, HEADER_POLL_INTERVAL_MS);
      }
    }

    pollForHeaders();

    const appLayoutHeaderContainer = document.querySelector(
      APP_LAYOUT_HEADER_SELECTOR
    );

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new window.ResizeObserver(() => {
      updateCalculatedHeights();
    });

    if (!hideHeader && appLayoutHeaderContainer) {
      observerRef.current.observe(appLayoutHeaderContainer);
    }

    if (includeProfileHeaders) {
      const profileHeaderContainer = getProfileHeaderContainerElement();
      if (profileHeaderContainer) {
        observerRef.current.observe(profileHeaderContainer);
      }
    }

    return () => {
      if (pollForHeadersId) {
        clearTimeout(pollForHeadersId);
      }

      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [
    featureFlags,
    updateCalculatedHeights,
    includeProfileHeaders,
    hideHeader,
  ]);

  useWindowResizeListener(updateCalculatedHeights, false, null);

  const spacingHeightStr =
    themeGet("space.r")({ theme }) || `${DEFAULT_SPACING_HEIGHT}px`;
  const spacingHeight = castToNumber(spacingHeightStr, DEFAULT_SPACING_HEIGHT);
  // 3× spacing when measuring profile chrome or when the app header is hidden (embed).
  const spacingMultiplier = includeProfileHeaders || hideHeader ? 3 : 2;

  const totalHeight = sum([
    spacingHeight * spacingMultiplier,
    profileHeaderHeight,
    appLayoutHeaderHeight,
  ]);

  if (
    (!isInitialized && totalHeight === 0) ||
    (!hideHeader && appLayoutHeaderHeight <= 0 && profileHeaderHeight <= 0)
  ) {
    return DEFAULT_FALLBACK_HEIGHT;
  }

  return totalHeight;
};

/**
 * @param {Object} [options]
 * @param {boolean} [options.includeProfileHeaders=true]
 * @param {boolean} [options.hideHeader=false]
 * @param {boolean} [options.useFlexParentHeight=false] — `100%` instead of `calc(100vh - …)` for flex parents.
 */
export const useAppHeadersHeightCalc = (options = {}) => {
  const { useFlexParentHeight = false, ...heightOptions } = options;

  const appHeadersHeight = useAppHeadersHeight(heightOptions);

  if (useFlexParentHeight) {
    return "100%";
  }

  const safeHeight = appHeadersHeight || DEFAULT_FALLBACK_HEIGHT;
  return `calc(100vh - ${safeHeight}px)`;
};
